import { Dispatch, SetStateAction, useCallback, useState } from 'react';

type ResettableStateResult<TState> = [TState, Dispatch<SetStateAction<TState>>];

type ResettableSnapshot<TState, TResetKey> = {
    resetKey: TResetKey;
    state: TState;
};

export function useResettableState<TState>(
    resetState: TState,
): ResettableStateResult<TState> {
    return useResettableStateWithKey(resetState, () => resetState);
}

export function useResettableStateWithKey<TState, TResetKey>(
    resetKey: TResetKey,
    getResetState: () => TState,
): ResettableStateResult<TState> {
    const [snapshot, setSnapshot] = useState<
        ResettableSnapshot<TState, TResetKey>
    >(() => ({
        resetKey,
        state: getResetState(),
    }));

    const state = Object.is(snapshot.resetKey, resetKey)
        ? snapshot.state
        : getResetState();

    const setState = useCallback(
        (action: SetStateAction<TState>) => {
            setSnapshot((prev) => {
                const current = Object.is(prev.resetKey, resetKey)
                    ? prev.state
                    : getResetState();

                return {
                    resetKey,
                    state:
                        typeof action === 'function'
                            ? (action as (value: TState) => TState)(current)
                            : action,
                };
            });
        },
        [resetKey, getResetState],
    );

    return [state, setState];
}
